import {
  CellularPayPrompt,
  CellularSimStatusController,
  CellularSimStatusSummary
} from './types'
import { formatDate, formatMoney } from './format'
import { trustedPendingAch } from './ach'

// What the customer is told about the SIMs a bill covers, stage by stage (plan
// decision 12: no emails, so this and the ERP's cellular tab are all they get).
// The paywall sends the stage and its dates; nothing here decides them.

export type PayableSimStage = 'past_due' | 'suspended' | 'on_hold'

export const PAST_DUE_MESSAGE = 'Your cellular bill is past due. Pay now to avoid suspension.'

// The past-due wording with the amount, so the customer knows what to pay.
export const pastDueMessage = (prompt: CellularPayPrompt): string =>
  prompt.amountDueUsd > 0
    ? `Your cellular bill of ${formatMoney(prompt.amountDueUsd, prompt.currency)} is past due. ` +
      'Pay now to avoid suspension.'
    : PAST_DUE_MESSAGE

// Least to most severe. A SIM on hold is suspended with its shut-off clock paused.
const SEVERITY: Record<string, number> = { past_due: 1, on_hold: 2, suspended: 3 }

const isPayableStage = (stage?: string | null): stage is PayableSimStage =>
  Boolean(stage && SEVERITY[stage])

const summaryOf = (prompt: CellularPayPrompt | null): CellularSimStatusSummary | null =>
  (prompt && prompt.simStatus) || null

const controllersOf = (prompt: CellularPayPrompt | null): CellularSimStatusController[] =>
  summaryOf(prompt)?.controllers || []

const mostSevere = (stages: string[]): PayableSimStage | null =>
  stages.filter(isPayableStage).reduce<PayableSimStage | null>(
    (worst, stage) => (!worst || SEVERITY[stage] > SEVERITY[worst] ? stage : worst),
    null
  )

// The stage the customer can still pay their way out of: the most severe among
// the SIMs that are not shut off for good. The summary's own stage is "most
// severe" over all of them, so a bill with one SIM shut off and one suspended
// reads 'deactivated' there, yet is still worth paying.
export const payableSimStage = (prompt: CellularPayPrompt | null): PayableSimStage | null => {
  const summary = summaryOf(prompt)
  if (!summary) return null
  const controllers = controllersOf(prompt)
  if (controllers.length === 0) return isPayableStage(summary.stage) ? summary.stage : null
  return mostSevere(controllers.map(controller => controller.stage))
}

// Every SIM on this bill is shut off for good: there is nothing left to pay for,
// so nothing locks and nothing offers to pay.
// The paywall says so with `payable: false`; an older answer without it is read
// from the stages.
export const isShutOff = (prompt: CellularPayPrompt | null): boolean =>
  Boolean(prompt && prompt.payable === false) ||
  (summaryOf(prompt)?.stage === 'deactivated' && payableSimStage(prompt) === null)

type DateField = 'suspendedAt' | 'shutOffOn' | 'deactivatedAt'

// The earliest value of a date field among the controllers in one stage.
const earliest = (controllers: CellularSimStatusController[], field: DateField): string | null =>
  controllers
    .map(controller => controller[field])
    .filter((value): value is string => Boolean(value))
    .sort((a, b) => Date.parse(a) - Date.parse(b))[0] || null

// A date for the stage being shown: the summary's when it is about that stage,
// otherwise the earliest among that stage's controllers.
const stageDate = (prompt: CellularPayPrompt, stage: string, field: DateField): string => {
  const summary = summaryOf(prompt)
  const inStage = controllersOf(prompt).filter(controller => controller.stage === stage)
  const own = summary && summary.stage === stage ? summary[field] : null
  return formatDate(own || earliest(inStage, field))
}

const suspendedSince = (prompt: CellularPayPrompt, stage: PayableSimStage): string => {
  const since = stageDate(prompt, stage, 'suspendedAt')
  return since ? `Cellular service has been suspended since ${since}.` : 'Cellular service is suspended.'
}

const shutOffDeadline = (prompt: CellularPayPrompt): string => {
  const on = stageDate(prompt, 'suspended', 'shutOffOn')
  return on
    ? `Pay by ${on} or it will be permanently shut off.`
    : 'Pay now or it will be permanently shut off.'
}

// Suspended while a trusted bank payment is on its way (the bill counts as paid):
// it stays off until the money lands, with no shut-off deadline.
export const BACK_ON_WHEN = {
  clearing: 'It turns back on once the bank payment clears (3–5 business days).',
  deposits: 'It turns back on once the test deposits are confirmed and the bank payment clears.'
}

const clearingMessage = (prompt: CellularPayPrompt, stage: PayableSimStage | null): string => {
  const ach = trustedPendingAch(prompt)
  if (!ach || !isSuspendedStage(stage)) return ''
  const wait = ach.state === 'waiting_deposits' ? BACK_ON_WHEN.deposits : BACK_ON_WHEN.clearing
  return `${suspendedSince(prompt, stage)} ${wait}`
}

// The owner's wording for the stage their bill is in; '' when every SIM is active.
export const simStageMessage = (prompt: CellularPayPrompt): string => {
  const stage = payableSimStage(prompt)
  const clearing = clearingMessage(prompt, stage)
  if (clearing) return clearing
  if (stage === 'past_due') return pastDueMessage(prompt)
  if (stage === 'suspended') return `${suspendedSince(prompt, stage)} ${shutOffDeadline(prompt)}`
  if (stage === 'on_hold') return `${suspendedSince(prompt, stage)} Pay now to restore it.`
  return ''
}

// The same stage for someone in the owner's workspace, who cannot pay it.
export const memberSimStageMessage = (prompt: CellularPayPrompt): string => {
  const stage = payableSimStage(prompt)
  if (stage === 'past_due') {
    return 'The cellular bill is past due, and service will be suspended unless it is paid.'
  }
  if (stage === 'suspended') {
    const on = stageDate(prompt, stage, 'shutOffOn')
    const unless = on ? ` It will be permanently shut off on ${on} unless the bill is paid.` : ''
    return `${suspendedSince(prompt, stage)}${unless}`
  }
  if (stage === 'on_hold') return `${suspendedSince(prompt, stage)} It stays off until the bill is paid.`
  return ''
}

export const shutOffMessage = (prompt: CellularPayPrompt): string => {
  const summary = summaryOf(prompt)
  const on = formatDate(summary?.deactivatedAt || earliest(controllersOf(prompt), 'deactivatedAt'))
  return on
    ? `Cellular service was permanently shut off on ${on}.`
    : 'Cellular service was permanently shut off.'
}

// A suspended SIM is what the customer notices on the sign, so its bill names them.
export const isSuspendedStage = (
  stage: PayableSimStage | null
): stage is 'suspended' | 'on_hold' =>
  stage === 'suspended' || stage === 'on_hold'

// The SIMs this bill covers are off (suspended or on hold): a bank payment on
// its way does not turn them back on until it clears.
export const isSimOff = (prompt: CellularPayPrompt): boolean => isSuspendedStage(payableSimStage(prompt))

const controllerLabel = ({ displayName, serial }: CellularSimStatusController): string => {
  if (displayName && serial) return `${displayName} (${serial})`
  return displayName || serial || ''
}

// "Main St Sign (MS-001)", one per affected controller; a SIM already shut off
// for good says so. Controllers with neither a name nor a serial are left out.
export const affectedControllers = (prompt: CellularPayPrompt): string[] =>
  controllersOf(prompt)
    .filter(controller => controller.stage !== 'active')
    .map(controller => {
      const name = controllerLabel(controller)
      if (!name) return ''
      return controller.stage === 'deactivated' ? `${name}, shut off` : name
    })
    .filter(Boolean)

// Banner-sized: the names in one sentence.
export const affectedSentence = (prompt: CellularPayPrompt): string => {
  const names = affectedControllers(prompt)
  return names.length ? `Affected: ${names.join('; ')}.` : ''
}
